import { useState, useEffect } from 'react'
import { useLocation } from 'wouter'
import { api } from '../api'
import { useAuth } from '../AuthContext'
import { useCachedFetch } from '../lib/swr'
import RinaContextCard from '../components/RinaContextCard'
import AccountMain from './account/AccountMain'
import { useActionsPortail } from '../contexts/navigation'
import PageHub from '../components/PageHub'
import { useCasquette } from '../contexts/casquette'
import { usePanier } from '../contexts/panier'
import { ROUTES } from '../routes'
import { MVP } from '../mvp'

// Page Compte — VUE PRINCIPALE seule (S56) : chaque sous-vue est une vraie
// Route d'App.jsx (pages/account/PageProfil, PageSecurite, PageChangerPin,
// PageAide, PageAideFaq, PageAideContact). Ici : la carte visiteur, la vue
// principale (AccountMain) et ses donnees (org producteur, solde wallet).

function CompteContenu() {
  const { isAuthenticated, user, token } = useAuth()
  const { hubMode, isProducer } = useCasquette()
  const { deconnecter: onLogout } = usePanier()
  const { navigate: onNavigate, setHubTab: onTabChange, changerCasquette } = useActionsPortail()
  const [, setLocation] = useLocation()

  // Bascule SUR PLACE depuis la page Compte (proto v7.4 pickLens)
  const onChangerCasquette = (m) => changerCasquette(m, { resterSurPlace: true })

  // Organisation producteur (pour carte devenir producteur)
  const [myOrg, setMyOrg] = useState(undefined)
  useEffect(() => {
    if (token && hubMode === 'client') {
      api.producer.getMe(token).then(org => setMyOrg(org || null)).catch(() => setMyOrg(null))
    }
  }, [token, hubMode])

  // Email « manquant » = absent ou fictif (`+596…@phone.manrina.local`, genere a
  // l'inscription par telephone). C'est lui qui declenche le bandeau « Profil a
  // completer » et le badge de la ligne « Mon profil » (proto v7.4).
  // MVP (S62) : pas de relance email tant que MVP.EMAIL_REQUIS est faux
  const emailManquant = MVP.EMAIL_REQUIS && (!user?.email || user.email.endsWith('@phone.manrina.local'))
  const initials = `${user?.prenom?.[0]?.toUpperCase() || '?'}${user?.nom?.[0]?.toUpperCase() || ''}`

  // Solde wallet du scope actif (casquette) — carte « Mon wallet » (proto v7.2.1).
  const { data: wallet } = useCachedFetch('wallet/me', api.getWallet, { enabled: isAuthenticated && MVP.WALLET })
  const soldeScope = Number(hubMode === 'producer' ? wallet?.solde?.producteur : wallet?.solde?.client) || 0
  const fmtSolde = (n) => (n < 0 ? '−' : '') + Math.abs(n).toFixed(2).replace('.', ',') + ' €'

  // ── VISITEUR (fidele prototype v5) ──
  if (!isAuthenticated) {
    return (
      <div className="glass-dark rounded-[26px] p-6 relative overflow-hidden text-center animate-fade-up">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(244,137,83,0.4), transparent 70%)' }} />
        <div className="relative">
          <span className="inline-flex w-16 h-16 rounded-full items-center justify-center text-3xl mb-3"
            style={{ background: 'rgba(247,240,234,0.12)', border: '2px solid rgba(247,240,234,0.4)' }}>👤</span>
          <p className="font-serif text-[23px] text-creme mb-1.5">Vous explorez en visiteur</p>
          <p className="text-[12.5px] text-creme/70 leading-relaxed mb-4">
            Créez un compte pour suivre vos commandes, garder vos favoris et devenir producteur si vous le souhaitez.
          </p>
          <button
            onClick={() => onNavigate('connexion')}
            className="w-full py-3.5 rounded-2xl text-white text-[15px] font-extrabold"
            style={{ background: 'linear-gradient(120deg, rgba(244,137,83,0.95), rgba(224,101,58,0.95))', boxShadow: '0 10px 26px rgba(244,137,83,0.32), inset 0 1px 0 rgba(255,255,255,0.35)' }}
          >
            Créer un compte · Se connecter
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <RinaContextCard message="Un souci avec ton compte ? Je suis la pour t'aider !" />
      <AccountMain
        tc="text-ink" ts="text-ink/60" cardBg="bg-card border-card-border"
        user={user} initials={initials} hubMode={hubMode} isProducer={isProducer}
        soldeScope={soldeScope} fmtSolde={fmtSolde} myOrg={myOrg} emailManquant={emailManquant}
        onChangerCasquette={onChangerCasquette} onTabChange={onTabChange}
        onNavigate={onNavigate} onLogout={onLogout}
        openProfile={() => setLocation(ROUTES.compte.profil)}
        openSecurite={() => setLocation(ROUTES.compte.securite)}
        openAide={() => setLocation(ROUTES.compte.aide)}
      />
    </>
  )
}

export default function PageCompte() {
  const { isProducer } = useCasquette()
  const { quitterCompte } = useActionsPortail()
  // MVP (S62) : sans barre basse, le Compte porte un « Retour » vers la derniere
  // page visitee HORS du Compte (accueil si on est arrive ici directement) —
  // pas l'historique navigateur, qui renvoyait vers /account/profil apres un
  // aller-retour Profil → « ← Compte » (remonte par Daniel).
  const sansBarre = !isProducer && !MVP.BARRE_CLIENT
  const retour = sansBarre ? { label: 'Retour', onClick: quitterCompte } : null
  return <PageHub retour={retour}><CompteContenu /></PageHub>
}
